/**
 * Сигнальный цвет темы, который можно читать как ТЕКСТ на заданном фоне.
 *
 * Палитра дизайн-системы подобрана под заливки и иконки, а не под текст: в светлой теме
 * её зелёный даёт на белом 2,67, жёлтый — 1,92, в тёмной красный на #2B2B34 — 2,17 при
 * норме WCAG AA 4,5 (QA 09.10, WS 15587410: «Реклама $0», «−$124», красные минусы в
 * таблице прогноза). Варианты antd `colorSuccessText` и т.п. совпадают с базовыми.
 *
 * Поэтому цвет темы не заменяется своим, а доводится до читаемости: смешивается с
 * чёрным (на светлом фоне) или белым (на тёмном) ровно настолько, насколько нужно.
 * Оттенок остаётся «тем самым зелёным» — меняется только светлота.
 */

/** Норма WCAG 2.x AA для обычного текста. */
export const MIN_TEXT_CONTRAST = 4.5;

const MIX_STEP = 0.05;

interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

const HEX_RE = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i;
const RGBA_RE = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i;

/** Токены темы приходят как `#rrggbb[aa]` или `rgb(a)(…)`; иное — ошибка, а не догадка. */
function parseColor(value: string): Rgba {
  const hex = HEX_RE.exec(value.trim());
  if (hex) {
    const n = Number.parseInt(hex[1], 16);
    const a = hex[2] === undefined ? 1 : Number.parseInt(hex[2], 16) / 255;
    return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff, a };
  }
  const rgba = RGBA_RE.exec(value.trim());
  if (rgba) {
    return { r: Number(rgba[1]), g: Number(rgba[2]), b: Number(rgba[3]), a: rgba[4] === undefined ? 1 : Number(rgba[4]) };
  }
  throw new Error(`readableColor: неизвестный формат цвета «${value}»`);
}

function toHex({ r, g, b }: Rgba): string {
  return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
}

/** Полупрозрачный цвет — так, как его видно поверх фона. */
function flatten(fg: Rgba, bg: Rgba): Rgba {
  return {
    r: fg.r * fg.a + bg.r * (1 - fg.a),
    g: fg.g * fg.a + bg.g * (1 - fg.a),
    b: fg.b * fg.a + bg.b * (1 - fg.a),
    a: 1,
  };
}

function luminance({ r, g, b }: Rgba): number {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function ratio(a: Rgba, b: Rgba): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Полупрозрачный `color`, сведённый с фоном `bg` в непрозрачный — тот же цвет, каким
 * его видно на этом фоне. Нужен там, где сквозь заливку не должно ничего проступать.
 */
export function opaqueOn(color: string, bg: string): string {
  return toHex(flatten(parseColor(color), parseColor(bg)));
}

/** Контраст текста `fg` на фоне `bg` по WCAG 2.x (полупрозрачный `fg` — поверх фона). */
export function contrastRatio(fg: string, bg: string): number {
  const background = parseColor(bg);
  return ratio(flatten(parseColor(fg), background), background);
}

/**
 * `color`, доведённый до контраста `min` на фоне `bg`. Уже читаемый цвет возвращается
 * как есть (в hex). Смешивание идёт к чёрному на светлом фоне и к белому на тёмном —
 * в пределе это чёрный или белый, которые на любом фоне дают ≥ 4,5.
 */
export function readableOn(color: string, bg: string, min: number = MIN_TEXT_CONTRAST): string {
  return readableOnAll(color, [bg], min);
}

/**
 * То же для текста, который стоит на нескольких фонах сразу (на карточке и на цветной
 * плашке того же оттенка): цвет доводится, пока не пройдёт на каждом. Направление —
 * по первому фону; плашки дизайн-системы той же светлоты, что и карточка.
 */
export function readableOnAll(color: string, backgrounds: readonly string[], min: number = MIN_TEXT_CONTRAST): string {
  if (backgrounds.length === 0) throw new Error('readableOnAll: не задан ни один фон');
  const parsed = backgrounds.map(parseColor);
  const base = flatten(parseColor(color), parsed[0]);
  const target: Rgba = luminance(parsed[0]) > 0.5 ? { r: 0, g: 0, b: 0, a: 1 } : { r: 255, g: 255, b: 255, a: 1 };
  for (let t = 0; t <= 1 + 1e-9; t += MIX_STEP) {
    const mixed: Rgba = {
      r: base.r + (target.r - base.r) * t,
      g: base.g + (target.g - base.g) * t,
      b: base.b + (target.b - base.b) * t,
      a: 1,
    };
    // Проверяется уже округлённый hex: округление каналов могло бы увести на волос ниже нормы.
    const hex = toHex(mixed);
    const candidate = parseColor(hex);
    if (parsed.every((background) => ratio(candidate, background) >= min)) return hex;
  }
  throw new Error(`readableOnAll: на фонах ${backgrounds.join(', ')} не достичь контраста ${min}`);
}
